import React from "react";

/**
 * Catches render-time crashes anywhere below it in the React tree and shows a
 * friendly fallback instead of blanking the entire page.
 *
 * Without this, a single TypeError (e.g., `pool.x` where pool is null) on one
 * page unmounts the whole app and the user sees a blank white screen — exactly
 * the production symptom we just hit.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    if (typeof window !== "undefined") {
      // eslint-disable-next-line no-console
      console.error("ErrorBoundary caught:", error, info?.componentStack);
    }
  }

  reset = () => {
    this.setState({ error: null });
  };

  reload = () => {
    if (typeof window !== "undefined") window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6">
        <div className="max-w-[420px] w-full bg-white rounded-2xl border border-rose-200 p-6 text-center" data-testid="error-boundary">
          <div className="w-12 h-12 mx-auto rounded-full bg-rose-50 text-rose-600 flex items-center justify-center font-extrabold text-[20px]">!</div>
          <h2 className="mt-4 text-[18px] font-bold text-slate-900">Something went wrong on this page</h2>
          <p className="mt-1.5 text-[13px] text-slate-500">
            We hit an unexpected error rendering this view. Your data is safe — just retry below.
          </p>
          {this.state.error?.message && (
            <pre className="mt-3 text-[11px] text-rose-600 bg-rose-50 rounded-lg p-2 overflow-x-auto text-left">{String(this.state.error.message).slice(0, 240)}</pre>
          )}
          <div className="mt-5 flex gap-2 justify-center">
            <button
              onClick={this.reset}
              className="px-4 h-10 rounded-full bg-slate-900 hover:bg-slate-700 text-white font-bold text-[13px]"
              data-testid="error-boundary-retry"
            >
              Retry
            </button>
            <button
              onClick={this.reload}
              className="px-4 h-10 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-900 font-bold text-[13px]"
              data-testid="error-boundary-reload"
            >
              Reload page
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
